'use client'

import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { InfoSetting } from './InfoSetting'
import { AppointmentSetting } from './AppointmentSetting'
import { useExaminationRoomById } from '@/hooks/useExaminationRoomById'
import { ExaminationRoom as BaseExaminationRoom } from '@/pages/clinic/examinationRooms/columns'
import { AppointmentManagement } from './AppointmentManagement'
import { QueueMachineCaller } from './QueueMachineCaller'
import IfAnyPermission from '@/components/IfAnyPermission'
import { AllowedAction } from '@/data/allowedActions'
import { Menu, X } from 'lucide-react'

// 預約掛號設定：涉及診所預約系統，admin/provider 管 clinic/clinic_group/clinic 本身都要可調整
const APPOINTMENT_SETTING_PERMISSIONS = [
  { module: 'system_clinic', action: AllowedAction.U },
  { module: 'provider_clinic_management', action: AllowedAction.U },
  { module: 'clinic_group_clinic_management', action: AllowedAction.U },
  { module: 'clinic_clinic_management', action: AllowedAction.U },
  { module: 'clinic_appointment_system', action: AllowedAction.U },
]

// 人流裝置設定：clinic-level 設定類動作
const PEOPLE_FLOW_DEVICE_PERMISSIONS = [
  { module: 'system_clinic', action: AllowedAction.U },
  { module: 'provider_clinic_management', action: AllowedAction.U },
  { module: 'clinic_group_clinic_management', action: AllowedAction.U },
  { module: 'clinic_clinic_management', action: AllowedAction.U },
]

// 擴展基本的診間類型，加入廣播設備相關屬性
type ExtendedExaminationRoom = BaseExaminationRoom & {
  broadcast_devices?: Array<{
    broadcast_device_id: number
    name?: string
    ip_address?: string
  }>
}

type SettingType =
  | 'info'
  | 'appointment'
  | 'appointmentManagement'
  | 'queueMachine'
  | 'marquee'
  | 'tvTheme'
  | 'banner'
  | 'tv_vod'
  | 'health_vod'
  | 'peopleFlowDevice'

type ExaminationRoomPageProps = {
  clinicId: number
  examinationRoomId?: number
  onMutate?: () => void
  onOpenChange?: (open: boolean) => void
}

export default function ExaminationRoomPage(props: ExaminationRoomPageProps) {
  const [activeTab, setActiveTab] = useState<SettingType>('info')
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const {
    examinationRoom,
    isLoading,
    mutate: mutateExaminationRoom,
  } = useExaminationRoomById(props.examinationRoomId ?? 0)

  // 使用 window.location 來讀取 URL 參數
  const getUrlParams = () => {
    if (typeof window === 'undefined') return { pageParam: null }

    const searchParams = new URLSearchParams(window.location.search)
    return {
      pageParam: searchParams.get('p'),
    }
  }

  // 監聽URL參數變化，自動切換到對應的tab
  useEffect(() => {
    const { pageParam } = getUrlParams()

    if (pageParam === 'queue_machine_caller') {
      setActiveTab('queueMachine')
    }
  }, [])

  const handleTabChange = (tab: SettingType) => {
    setActiveTab(tab)
    // 在移動端選擇後自動關閉側邊欄
    setSidebarOpen(false)
  }

  const handleSuccess = () => {
    if (props?.onMutate) props.onMutate()
    mutateExaminationRoom()
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'info':
        return (
          <InfoSetting
            examinationRoom={examinationRoom}
            isLoading={isLoading}
            onSuccess={handleSuccess}
          />
        )

      case 'appointment':
        return (
          <AppointmentSetting
            examinationRoom={examinationRoom}
            isLoading={isLoading}
            onSuccess={handleSuccess}
          />
        )

      case 'appointmentManagement':
        return (
          <AppointmentManagement
            examinationRoom={examinationRoom}
            isLoading={isLoading}
            onSuccess={handleSuccess}
          />
        )

      case 'queueMachine':
        return (
          <QueueMachineCaller
            examinationRoom={examinationRoom}
            isLoading={isLoading}
            onSuccess={handleSuccess}
          />
        )

      default:
        return null
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 md:px-6 py-4 border-b bg-card flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* 漢堡選單按鈕 - 只在移動端顯示 */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>
            <div>
              <h2 className="text-xl md:text-2xl font-bold tracking-tight">
                {isLoading
                  ? '載入中...'
                  : `${(examinationRoom as ExtendedExaminationRoom)?.name || '未命名'} 診間設定`}
              </h2>
              <p className="text-sm text-muted-foreground mt-1 hidden sm:block">
                在這裡管理診間的基本資訊與相關設定
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex overflow-hidden relative" style={{ height: 'calc(90vh - 80px)' }}>
        {/* 移動端覆蓋層 */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div
          className={cn(
            'w-48 border-r bg-white flex-shrink-0 overflow-y-auto z-50',
            'md:relative md:translate-x-0',
            'fixed left-0 top-0 h-full transition-transform duration-300 ease-in-out',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <div className="flex items-center justify-between p-4 border-b md:hidden">
            <span className="font-semibold">選單</span>
            <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <nav className="p-4 space-y-2">
            <Button
              variant={activeTab === 'info' ? 'secondary' : 'ghost'}
              className={cn('w-full justify-start')}
              onClick={() => handleTabChange('info')}
            >
              基本資料
            </Button>
            <Button
              variant={activeTab === 'queueMachine' ? 'secondary' : 'ghost'}
              className={cn('w-full justify-start')}
              onClick={() => handleTabChange('queueMachine')}
            >
              叫號機
            </Button>
            <IfAnyPermission permissions={APPOINTMENT_SETTING_PERMISSIONS}>
              <Button
                variant={activeTab === 'appointment' ? 'secondary' : 'ghost'}
                className={cn('w-full justify-start')}
                onClick={() => handleTabChange('appointment')}
              >
                預約掛號設定
              </Button>
            </IfAnyPermission>
            <Button
              variant={activeTab === 'appointmentManagement' ? 'secondary' : 'ghost'}
              className={cn('w-full justify-start')}
              onClick={() => handleTabChange('appointmentManagement')}
            >
              預約掛號管理
            </Button>
            <IfAnyPermission permissions={PEOPLE_FLOW_DEVICE_PERMISSIONS}>
              <Button
                variant={activeTab === 'peopleFlowDevice' ? 'secondary' : 'ghost'}
                className={cn('w-full justify-start')}
                onClick={() => handleTabChange('peopleFlowDevice')}
              >
                人流裝置設定
              </Button>
            </IfAnyPermission>
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto px-4 md:px-6 pb-6">
          <div className="pt-5">{renderContent()}</div>
        </div>
      </div>
    </div>
  )
}
